import { createFileRoute, Link } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { helpArticles, helpCategories } from '@/content/help/registry'
import { BRAND_MINT } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { ArrowRightIcon, SearchIcon } from 'lucide-react'

export const Route = createFileRoute('/dashboard/help/')({
  component: HelpIndexPage,
})

function HelpIndexPage() {
  const [query, setQuery] = useState('')
  const q = query.trim().toLowerCase()

  const filtered = useMemo(() => {
    if (!q) return helpArticles
    return helpArticles.filter((a) =>
      [a.title, a.description, a.category].some((s) => s.toLowerCase().includes(q)),
    )
  }, [q])

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6 flex items-start gap-3">
        <span
          aria-hidden="true"
          className="mt-0.5 shrink-0"
          style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }}
        />
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight">Help &amp; training</h1>
          <p className="text-sm text-muted-foreground">
            How-to guides for running your payroll, one task at a time.
          </p>
        </div>
      </div>

      <div className="relative mb-8">
        <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search how-to guides…"
          aria-label="Search how-to guides"
          className="pl-9"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-xl border border-border bg-card p-10 text-center">
          <p className="text-sm font-medium text-foreground">No guides found</p>
          <p className="mt-1 text-sm text-muted-foreground">Try a different search term.</p>
        </div>
      ) : (
        helpCategories.map((category) => {
          const articles = filtered.filter((a) => a.category === category)
          if (articles.length === 0) return null
          return (
            <section key={category} className="mb-8 last:mb-0">
              <h2 className="mb-3 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground/60">
                {category}
              </h2>
              <div className="grid gap-3 sm:grid-cols-2">
                {articles.map((article) => (
                  <Link
                    key={article.slug}
                    to="/dashboard/help/$slug"
                    params={{ slug: article.slug }}
                    className="group flex flex-col gap-1 rounded-xl border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:shadow-md"
                  >
                    <span className="flex items-center gap-2 text-sm font-semibold text-foreground transition-colors group-hover:text-primary">
                      {article.title}
                      <ArrowRightIcon className="size-3.5 shrink-0 text-muted-foreground transition-all group-hover:translate-x-0.5 group-hover:text-foreground" />
                    </span>
                    <span className="text-xs text-muted-foreground">{article.description}</span>
                  </Link>
                ))}
              </div>
            </section>
          )
        })
      )}
    </div>
  )
}